import { useMemo } from 'react';
import moment from 'moment';
import queryString from 'query-string';
import slugify from 'slugify';
import { firmWaterfallUrl, waterfallUrl } from 'common/config/urls';
import type { TertiaryNavigationLinkItem } from 'context/FirmBaseLayoutContext';
import type { WaterfallView } from 'pages/Waterfall/api/waterfallViews';
import { MOMENT_DEFAULT_DATE_FORMAT } from 'utilities';
import {
  WATERFALL_CURRENCY_FORMAT,
  WATERFALL_QUERY_PARAM_CAP_TABLE_VERSION,
  WATERFALL_QUERY_PARAM_CURRENCY_FORMAT,
  WATERFALL_QUERY_PARAM_EXIT_DATE,
  WATERFALL_QUERY_PARAM_EXIT_ENTERPRISE_VALUE,
  WATERFALL_QUERY_PARAM_EXIT_EQUITY_VALUE,
  WATERFALL_QUERY_PARAM_FOREIGN_CURRENCY,
  WATERFALL_QUERY_PARAM_FOREIGN_EXCHANGE_RATE,
  WATERFALL_QUERY_PARAM_FOREIGN_EXCHANGE_RATE_INPUT_CURRENCY,
  WATERFALL_QUERY_PARAM_FOREIGN_EXCHANGE_RATE_OVERRIDDEN,
  WATERFALL_QUERY_PARAM_LESS_DEBT,
  WATERFALL_QUERY_PARAM_PLUS_CASH,
  WATERFALL_QUERY_PARAM_VIEW,
  WATERFALL_UNITS_TO_CURRENCY_FORMAT,
} from '../../constants';
import type { WaterfallUrlState } from '../useWaterfallUrlState/useWaterfallUrlState.props';

const DEFAULT_VIEW_VALUE = '__default__';
// Saved View rates round-trip through a DecimalField with 15 decimal places.
const EXCHANGE_RATE_COMPARISON_TOLERANCE = 1e-14;

interface UseWaterfallViewNavItemsParams {
  views: WaterfallView[];
  urlState: WaterfallUrlState;
  isFirmPage: boolean;
  firmSlug: string;
  companySlug: string;
  companyList?: Array<{ company_id: number; company_name?: string }> | null;
  onSelectDefault?: () => void;
  onSelectView?: (viewId: number) => void;
}

interface WaterfallViewNavItemsResult {
  items: TertiaryNavigationLinkItem[];
  activeValue: string;
  activeViewId: number | null;
  hasRequestedView: boolean;
  matchedViewId: number | null;
}

const nullishEqual = (a: unknown, b: unknown) => {
  const aEmpty = a == null || a === '';
  const bEmpty = b == null || b === '';
  if (aEmpty && bEmpty) {
    return true;
  }
  if (aEmpty !== bEmpty) {
    return false;
  }
  return String(a) === String(b);
};

const numericEqual = (urlVal: number | undefined, viewVal: string | null | undefined) => {
  const urlEmpty = urlVal == null;
  const viewEmpty = viewVal == null;
  if (urlEmpty && viewEmpty) {
    return true;
  }
  if (urlEmpty !== viewEmpty) {
    return false;
  }
  return Math.abs(Number(urlVal) - Number(viewVal)) < 0.001;
};

const exchangeRateEqual = (urlVal: number | undefined, viewVal: string | null | undefined) => {
  const urlEmpty = urlVal == null;
  const viewEmpty = viewVal == null;
  if (urlEmpty && viewEmpty) {
    return true;
  }
  if (urlEmpty !== viewEmpty) {
    return false;
  }
  return Math.abs(Number(urlVal) - Number(viewVal)) < EXCHANGE_RATE_COMPARISON_TOLERANCE;
};

// exitDateFromUrl is in MM/DD/YYYY; view.exit_date from backend is ISO YYYY-MM-DD. Normalize to ISO.
const toIsoDate = (d: string | null | undefined): string | null | undefined => {
  if (d == null) {
    return d;
  }
  const m = moment(d, MOMENT_DEFAULT_DATE_FORMAT, true);
  return m.isValid() ? m.format('YYYY-MM-DD') : d;
};

const viewMatchesUrl = (view: WaterfallView, urlState: WaterfallUrlState, isFirmPage: boolean): boolean => {
  const foreignCurrency = view.foreign_currency ?? 'USD';
  const currencyFormat = view.currency_format ?? WATERFALL_CURRENCY_FORMAT.MILLIONS;
  const isExchangeRateOverridden = view.foreign_exchange_rate_is_overridden ?? false;
  const exchangeRateInputCurrency = view.foreign_exchange_rate_input_currency ?? 'foreign';

  if (isFirmPage && !nullishEqual(urlState.companyIdFromUrl, view.company)) {
    return false;
  }
  if (!nullishEqual(urlState.measurementDateValueFromUrl, view.measurement_date_value)) {
    return false;
  }
  if (!nullishEqual(urlState.captableIdFromUrl, view.cap_table)) {
    return false;
  }
  if (!nullishEqual(toIsoDate(urlState.exitDateFromUrl), view.exit_date)) {
    return false;
  }
  if (!numericEqual(urlState.equityValueFromUrl, view.exit_equity_value)) {
    return false;
  }
  if (!numericEqual(urlState.enterpriseValueFromUrl, view.exit_enterprise_value)) {
    return false;
  }
  if (!numericEqual(urlState.cashOverrideFromUrl, view.plus_cash)) {
    return false;
  }
  if (!numericEqual(urlState.debtOverrideFromUrl, view.less_debt)) {
    return false;
  }
  if (!nullishEqual(urlState.foreignCurrencyFromUrl, foreignCurrency)) {
    return false;
  }
  if (!nullishEqual(WATERFALL_UNITS_TO_CURRENCY_FORMAT[urlState.currencyUnitsFromUrl], currencyFormat)) {
    return false;
  }
  if (!exchangeRateEqual(urlState.foreignExchangeRateFromUrl, view.foreign_exchange_rate)) {
    return false;
  }
  if (urlState.isForeignExchangeRateOverriddenFromUrl !== isExchangeRateOverridden) {
    return false;
  }
  if (
    isExchangeRateOverridden &&
    !nullishEqual(urlState.foreignExchangeRateInputCurrencyFromUrl, exchangeRateInputCurrency)
  ) {
    return false;
  }
  return true;
};

const buildViewUrl = (
  view: WaterfallView,
  isFirmPage: boolean,
  firmSlug: string,
  companySlug: string,
  companyList?: Array<{ company_id: number; company_name?: string }> | null
): string => {
  const basePath = isFirmPage ? firmWaterfallUrl(firmSlug) : waterfallUrl(firmSlug, companySlug);

  const params: Record<string, string> = {};

  if (view.id != null) {
    params[WATERFALL_QUERY_PARAM_VIEW] = String(view.id);
  }

  if (view.measurement_date_value) {
    params.date = view.measurement_date_value;
  }

  if (view.cap_table != null) {
    params[WATERFALL_QUERY_PARAM_CAP_TABLE_VERSION] = `captable-${view.cap_table}`;
  }

  if (view.exit_date) {
    params[WATERFALL_QUERY_PARAM_EXIT_DATE] = view.exit_date;
  }

  if (view.exit_equity_value != null) {
    params[WATERFALL_QUERY_PARAM_EXIT_EQUITY_VALUE] = view.exit_equity_value;
  }

  if (view.exit_enterprise_value != null) {
    params[WATERFALL_QUERY_PARAM_EXIT_ENTERPRISE_VALUE] = view.exit_enterprise_value;
  }

  if (view.plus_cash != null) {
    params[WATERFALL_QUERY_PARAM_PLUS_CASH] = view.plus_cash;
  }

  if (view.less_debt != null) {
    params[WATERFALL_QUERY_PARAM_LESS_DEBT] = view.less_debt;
  }

  params[WATERFALL_QUERY_PARAM_FOREIGN_CURRENCY] = view.foreign_currency ?? 'USD';
  params[WATERFALL_QUERY_PARAM_CURRENCY_FORMAT] = view.currency_format ?? WATERFALL_CURRENCY_FORMAT.MILLIONS;

  if (view.foreign_exchange_rate != null) {
    params[WATERFALL_QUERY_PARAM_FOREIGN_EXCHANGE_RATE] = view.foreign_exchange_rate;
  }

  if (view.foreign_exchange_rate_is_overridden ?? false) {
    params[WATERFALL_QUERY_PARAM_FOREIGN_EXCHANGE_RATE_OVERRIDDEN] = 'true';
    params[WATERFALL_QUERY_PARAM_FOREIGN_EXCHANGE_RATE_INPUT_CURRENCY] =
      view.foreign_exchange_rate_input_currency ?? 'foreign';
  }

  if (isFirmPage) {
    const companyName = companyList?.find(c => c.company_id === view.company)?.company_name ?? 'company';
    const slug = `${slugify(companyName, { lower: true })}-${view.company}`;
    params.company = slug;
  }

  const qs = queryString.stringify(params);
  return qs ? `${basePath}?${qs}` : basePath;
};

const useWaterfallViewNavItems = ({
  views,
  urlState,
  isFirmPage,
  firmSlug,
  companySlug,
  companyList,
  onSelectDefault,
  onSelectView,
}: UseWaterfallViewNavItemsParams): WaterfallViewNavItemsResult => {
  const basePath = isFirmPage ? firmWaterfallUrl(firmSlug) : waterfallUrl(firmSlug, companySlug);
  const currentViewUrl = useMemo(() => {
    if (!isFirmPage || urlState.companyIdFromUrl == null) {
      return basePath;
    }

    const companyName = companyList?.find(c => c.company_id === urlState.companyIdFromUrl)?.company_name ?? 'company';
    const params: Record<string, string> = {
      company: `${slugify(companyName, { lower: true })}-${urlState.companyIdFromUrl}`,
    };

    if (urlState.measurementDateValueFromUrl) {
      params.date = urlState.measurementDateValueFromUrl;
    }

    return `${basePath}?${queryString.stringify(params)}`;
  }, [basePath, companyList, isFirmPage, urlState.companyIdFromUrl, urlState.measurementDateValueFromUrl]);

  const items = useMemo<TertiaryNavigationLinkItem[]>(() => {
    const defaultItem: TertiaryNavigationLinkItem = {
      value: DEFAULT_VIEW_VALUE,
      label: 'Current',
      // onSelect resets URL-default init flags; to performs the actual navigation.
      // The layout calls onSelect first, then navigate(to), so defaults re-fire on the new URL.
      onSelect: onSelectDefault,
      to: currentViewUrl,
    };

    const viewItems: TertiaryNavigationLinkItem[] = views.map(view => {
      const viewId = view.id;
      return {
        value: String(viewId),
        label: view.name,
        to: buildViewUrl(view, isFirmPage, firmSlug, companySlug, companyList),
        showMenu: true,
        onSelect: onSelectView && viewId != null ? () => onSelectView(viewId) : undefined,
      };
    });

    return [defaultItem, ...viewItems];
  }, [views, isFirmPage, firmSlug, companySlug, companyList, currentViewUrl, onSelectView, onSelectDefault]);

  const matchedViewId = useMemo<number | null>(() => {
    const requestedView = views.find(view => view.id === urlState.viewIdFromUrl);
    if (requestedView && viewMatchesUrl(requestedView, urlState, isFirmPage)) {
      return requestedView.id ?? null;
    }

    const match = views.find(v => viewMatchesUrl(v, urlState, isFirmPage));
    return match?.id ?? null;
  }, [views, urlState, isFirmPage]);

  const hasRequestedView = urlState.parsedQuery[WATERFALL_QUERY_PARAM_VIEW] != null;
  const activeViewId = useMemo<number | null>(
    () => views.find(view => view.id === urlState.viewIdFromUrl)?.id ?? null,
    [urlState.viewIdFromUrl, views]
  );

  const activeValue = activeViewId != null ? String(activeViewId) : DEFAULT_VIEW_VALUE;

  return { items, activeValue, activeViewId, hasRequestedView, matchedViewId };
};

export default useWaterfallViewNavItems;
export { DEFAULT_VIEW_VALUE };
export type { UseWaterfallViewNavItemsParams, WaterfallViewNavItemsResult };
